/**
 * 숫자 · 금액 표시. `Intl.NumberFormat` 만 쓴다.
 * - 값 없음(null · undefined · NaN · Infinity)은 자리표시 하나(`-`)로 통일한다.
 * - 큰 수의 단위 배율(천 · 백만 · 억)은 값마다 붙이지 않고 열 머리 · 축 제목에 한 번 적는다
 *   → `scaleNumber` 로 값만 줄이고 `NUMBER_SCALE_LABEL` 을 머리글에 쓴다.
 * - 숫자 열은 오른쪽 정렬 + `tabular-nums`.
 */
export const EMPTY_PLACEHOLDER = '-';
export const DISPLAY_LOCALE = 'ko-KR';

export type Numeric = number | null | undefined;

export interface NumberFormatOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** 양수에도 `+` 를 붙인다(증감 표시) */
  signed?: boolean;
}

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(DISPLAY_LOCALE, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

export function isEmptyNumber(value: Numeric): value is null | undefined {
  return value === null || value === undefined || !Number.isFinite(value);
}

export function formatNumber(value: Numeric, options: NumberFormatOptions = {}): string {
  if (isEmptyNumber(value)) return EMPTY_PLACEHOLDER;
  return getFormatter({
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
    signDisplay: options.signed ? 'exceptZero' : 'auto',
  }).format(value);
}

/** 비율(0.1234)을 퍼센트(`12.3%`)로. 이미 퍼센트 값이면 `formatNumber` 뒤에 `%` 를 붙인다. */
export function formatPercent(ratio: Numeric, options: NumberFormatOptions = {}): string {
  if (isEmptyNumber(ratio)) return EMPTY_PLACEHOLDER;
  return getFormatter({
    style: 'percent',
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
    signDisplay: options.signed ? 'exceptZero' : 'auto',
  }).format(ratio);
}

export type CurrencyCode = 'KRW' | 'USD' | 'JPY' | 'EUR' | 'CNY';

export function formatCurrency(value: Numeric, currency: CurrencyCode): string {
  if (isEmptyNumber(value)) return EMPTY_PLACEHOLDER;
  return getFormatter({ style: 'currency', currency }).format(value);
}

export const NUMBER_SCALE = {
  one: 1,
  thousand: 1_000,
  million: 1_000_000,
  /** 억 */
  hundredMillion: 100_000_000,
} as const;

export type NumberScale = keyof typeof NUMBER_SCALE;

export const NUMBER_SCALE_LABEL: Record<NumberScale, string> = {
  one: '',
  thousand: '천',
  million: '백만',
  hundredMillion: '억',
};

/** 값을 배율로 나눠 표시한다. 단위 글자는 붙이지 않는다(머리글에 한 번). */
export function scaleNumber(
  value: Numeric,
  scale: NumberScale,
  options: NumberFormatOptions = {},
): string {
  if (isEmptyNumber(value)) return EMPTY_PLACEHOLDER;
  return formatNumber(value / NUMBER_SCALE[scale], options);
}

/** 값 없음을 자리표시로 바꾼다(문자열 칸). */
export function orPlaceholder(value: string | null | undefined): string {
  return value === null || value === undefined || value.trim() === '' ? EMPTY_PLACEHOLDER : value;
}
